<section class="provider-profile-page">

  <!-- ── Header ─────────────────────────────────────────────────────────── -->
  <div class="page-header">
    <div class="header-left">
      <button class="ghost-btn back-btn" type="button" (click)="back()">
        <mat-icon aria-hidden="true">arrow_back</mat-icon>
        Back
      </button>
      @if (provider()) {
        <h1>{{ provider()!.name }}</h1>
      } @else {
        <h1>Provider Profile</h1>
      }
    </div>
    @if (provider() && isSuperAdmin()) {
      <a class="ghost-btn" [routerLink]="['/providers', providerId, 'edit']">
        <mat-icon aria-hidden="true">edit</mat-icon>
        Edit
      </a>
    }
  </div>

  <!-- ── Loading ────────────────────────────────────────────────────────── -->
  @if (loading()) {
    <div class="page-loading">
      <mat-progress-spinner mode="indeterminate" diameter="36"></mat-progress-spinner>
    </div>

  } @else if (error()) {
    <div class="page-error">{{ error() }}</div>

  } @else if (provider(); as p) {

    <!-- ── Body: 75% / 25% ─────────────────────────────────────────────── -->
    <div class="page-body">

      <!-- ════════ LEFT COLUMN ════════ -->
      <div class="left-col">

        <!-- ── Identity ──────────────────────────────────────────────────── -->
        <div class="section-card">
          <div class="section-header">
            <mat-icon class="section-icon">badge</mat-icon>
            <h2 class="section-title">Identity</h2>
            <span class="status-chip" [class.status-chip--active]="p.is_active" [class.status-chip--inactive]="!p.is_active">
              {{ p.is_active ? 'Active' : 'Inactive' }}
            </span>
          </div>

          <div class="fields-grid">

            @if (p.contact_name) {
              <div class="field">
                <span class="field-label">Contact</span>
                <span class="field-value">{{ p.contact_name }}</span>
              </div>
            }

            @if (p.phone) {
              <div class="field">
                <span class="field-label">Phone</span>
                <span class="field-value">{{ p.phone }}</span>
              </div>
            }

            @if (p.secondary_phone) {
              <div class="field">
                <span class="field-label">Alt. phone</span>
                <span class="field-value">{{ p.secondary_phone }}</span>
              </div>
            }

            @if (p.email) {
              <div class="field">
                <span class="field-label">Email</span>
                <span class="field-value">{{ p.email }}</span>
              </div>
            }

            <div class="field">
              <span class="field-label">Preferred contact</span>
              <span class="field-value">{{ contactMethodLabel(p.preferred_contact_method) }}</span>
            </div>

            @if (p.languages.length) {
              <div class="field">
                <span class="field-label">Languages</span>
                <span class="field-value">{{ p.languages.map(languageLabel).join(', ') }}</span>
              </div>
            }

            <div class="field">
              <span class="field-label">Added</span>
              <span class="field-value field-value--muted">{{ formatDate(p.created_at) }}</span>
            </div>

          </div>
        </div>

        <!-- ── Coverage ──────────────────────────────────────────────────── -->
        <div class="section-card">
          <div class="section-header">
            <mat-icon class="section-icon">location_on</mat-icon>
            <h2 class="section-title">Coverage</h2>
          </div>

          <div class="fields-grid">

            <div class="field field--wide">
              <span class="field-label">Cities</span>
              <span class="field-value">{{ cityLabels(p.city_codes) }}</span>
            </div>

            <div class="field">
              <span class="field-label">Mon–Fri hours</span>
              <span class="field-value">{{ typicalWeekdayHours(p.working_hours) }}</span>
            </div>

            @if (weekendHours(p.working_hours); as wknd) {
              <div class="field">
                <span class="field-label">Weekend</span>
                <span class="field-value">{{ wknd }}</span>
              </div>
            }

          </div>
        </div>

        <!-- ── Services + Internal notes (side by side) ──────────────────── -->
        <div class="mid-row">

          <!-- Services -->
          <div class="section-card">
            <div class="section-header">
              <mat-icon class="section-icon">home_repair_service</mat-icon>
              <h2 class="section-title">Services</h2>
            </div>

            <div class="fields-stack">

              <div class="field field--wide">
                <span class="field-label">Categories</span>
                <span class="field-value">{{ categoryNames(p.category_ids) }}</span>
              </div>

              <div class="field field--wide">
                <span class="field-label">Service tags</span>
                <span class="field-value field-value--placeholder">— not configured</span>
              </div>

              <div class="field field--wide">
                <span class="field-label">Exclusions</span>
                <span class="field-value field-value--placeholder">— not configured</span>
              </div>

            </div>
          </div>

          <!-- Internal notes -->
          <div class="section-card">
            <div class="section-header">
              <mat-icon class="section-icon">sticky_note_2</mat-icon>
              <h2 class="section-title">Operator Notes</h2>
            </div>

            <div class="fields-stack">

              @if (p.admin_notes) {
                <p class="notes-text">{{ p.admin_notes }}</p>
              } @else {
                <p class="notes-text notes-text--empty">No notes added.</p>
              }

              @if (p.internal_notes) {
                <div class="field field--wide" style="margin-top: 12px;">
                  <span class="field-label">Internal</span>
                  <p class="notes-text notes-text--internal">{{ p.internal_notes }}</p>
                </div>
              }

            </div>
          </div>

        </div>

        <!-- ── Do not send rules ─────────────────────────────────────────── -->
        <div class="section-card section-card--placeholder">
          <div class="section-header">
            <mat-icon class="section-icon section-icon--muted">block</mat-icon>
            <h2 class="section-title section-title--muted">Do Not Send Rules</h2>
          </div>
          <p class="placeholder-text">Rule configuration coming soon — define conditions under which this provider should not be dispatched.</p>
        </div>

      </div>
      <!-- end left-col -->

      <!-- ════════ RIGHT COLUMN ════════ -->
      <div class="right-col">

        <!-- ── Trust & Verification ───────────────────────────────────────── -->
        <div class="section-card">
          <div class="section-header">
            <mat-icon class="section-icon">verified_user</mat-icon>
            <h2 class="section-title">Trust & Verification</h2>
          </div>

          <div class="fields-stack">

            <div class="field field--wide">
              <span class="field-label">Trust status</span>
              <span class="trust-chip trust-chip--{{ p.trust_status }}">
                {{ trustLabel(p.trust_status) }}
              </span>
            </div>

            <div class="field field--wide">
              <span class="field-label">Liability insurance</span>
              @if (p.insurance_verified) {
                <span class="bool-chip bool-chip--yes">Verified</span>
              } @else {
                <span class="bool-chip bool-chip--no">Not verified</span>
              }
            </div>

            <div class="field field--wide">
              <span class="field-label">WCB registered</span>
              @if (p.wcb_registered) {
                <span class="bool-chip bool-chip--yes">Yes</span>
              } @else {
                <span class="bool-chip bool-chip--no">No</span>
              }
            </div>

            @if (p.verified_at) {
              <div class="field field--wide">
                <span class="field-label">Verified on</span>
                <span class="field-value field-value--muted">{{ formatDate(p.verified_at) }}</span>
              </div>
            }

          </div>
        </div>

        <!-- ── Pricing Notes ──────────────────────────────────────────────── -->
        <div class="section-card section-card--placeholder">
          <div class="section-header">
            <mat-icon class="section-icon section-icon--muted">attach_money</mat-icon>
            <h2 class="section-title section-title--muted">Pricing Notes</h2>
          </div>
          <p class="placeholder-text">Pricing configuration coming soon.</p>
        </div>

        <!-- ── Recent Jobs ────────────────────────────────────────────────── -->
        <div class="section-card section-card--placeholder">
          <div class="section-header">
            <mat-icon class="section-icon section-icon--muted">work_history</mat-icon>
            <h2 class="section-title section-title--muted">Recent Jobs</h2>
          </div>
          <p class="placeholder-text">Recent job history coming soon.</p>
        </div>

      </div>
      <!-- end right-col -->

    </div>
    <!-- end page-body -->

  }

</section>